import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import {
  signUp,
  signIn,
  confirmSignIn,
  autoSignIn,
  confirmSignUp,
  resendSignUpCode,
  fetchAuthSession,
  signOut,
  signInWithRedirect,
  resetPassword,
  confirmResetPassword,
  updatePassword,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { environment } from '../../../../../environments/environment';

export interface RegisterResult {
  success: boolean;
  needsConfirmation?: boolean;
  autoSignedIn?: boolean;
  error?: string;
  errorName?: string;
}

interface ProviderCheckResult {
  success: boolean;
  error?: string;
  errorName?: string;
}

// Held in memory for the duration of the verification step.
// cognitoSub is captured from signUp() and used by the pre-register call.
interface PendingRegistration {
  cognitoSub: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  marketingConsent: boolean;
}

export interface UserProfile {
  id: number;
  email: string;
  first_name: string | null;
  last_name: string | null;
  city: string | null;
  phone: string | null;
  doc_type: 'CC' | 'TI' | 'CE' | 'PA' | 'NIT' | null;
  doc_number: string | null;
  birth_date: string | null;
  registered_at: string;
  has_active_subscription: boolean;
  marketing_consent: boolean;
}

export interface UpdateProfilePayload {
  first_name: string;
  last_name: string;
  phone: string | null;
  city: string | null;
  birth_date: string | null;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);

  // JWT stored in memory — never in localStorage or sessionStorage
  private accessToken = signal<string | null>(null);
  private idToken = signal<string | null>(null);

  // sessionStorage key used to persist the external token (from WordPress redirect) across page refreshes
  private readonly EXTERNAL_TOKEN_KEY = 'lacronica_external_token';

  // localStorage flag set by logout() and consumed by hasActiveSession().
  // Guarantees the login modal shows the form after logout even if Cognito
  // global sign-out fails (network error, config issue, etc.).
  private readonly FORCE_LOGOUT_KEY = 'lacronica_force_logout';
  private readonly PROVIDER_MISMATCH_KEY = 'lacronica_provider_mismatch';

  // Temporary data kept until email is confirmed
  private pendingRegistration: PendingRegistration | null = null;

  // True while loginWithEmail() is in progress — prevents the Hub listener
  // from treating an email/password sign-in as an OAuth sign-in
  private emailLoginInProgress = false;

  // Set to true after any successful login/register so the panel can sho w a welcome toast once
  readonly justLoggedIn = signal(false);

  // Stores the authenticated user's profile — populated on login/register, null on logout
  readonly currentUser = signal<UserProfile | null>(null);

  // Derived signal: true when the current user has an active subscription
  readonly hasActiveSubscription = signal(false);

  readonly oauthProviderError = signal<string | null>(null);

  /** Central setter — always updates both currentUser and hasActiveSubscription together. */
  private setCurrentUser(user: UserProfile | null): void {
    this.currentUser.set(user);
    this.hasActiveSubscription.set(user?.has_active_subscription === true);
  }

  constructor() {
    // Listen for OAuth sign-in events (e.g., Google via signInWithRedirect).
    // When Amplify completes the code exchange after a redirect, it fires
    // 'signedIn'. We grab the token and upsert the profile in the backend.
    Hub.listen('auth', async ({ payload }) => {
      if (payload.event === 'signedIn') {
        // Only handle OAuth (Google/Facebook) sign-ins here.
        // Email and registration flows manage their own session token internally.
        if (!this.pendingRegistration && !this.emailLoginInProgress) {
          // If a logout just happened, ignore this event — it can fire unexpectedly
          // in some Amplify v6 builds when the session is restored from storage.
          // Do NOT consume the flag here; hasActiveSession() must do that.
          try { if (localStorage.getItem(this.FORCE_LOGOUT_KEY)) return; } catch { /* no-op */ }
          // Belt-and-suspenders: also check the URL param (handles Chrome Storage
          // Partitioning where the localStorage flag may be in a different partition).
          try {
            if (new URLSearchParams(window.location.search).get('force_logout') === '1') {
              console.log('[ELD-AUTH] Hub signedIn ignorado — force_logout=1 en URL');
              return;
            }
          } catch { /* no-op */ }
          await this.loadSessionToken();
          const synced = await this.syncOAuthProfileToBackend();
          if (synced) {
            this.saveWpSession();
            this.justLoggedIn.set(true);
            // Use postEmbeddedSessionToParent() instead of a raw postMessage so
            // that BOTH contexts are handled correctly:
            //   · iframe  → posts to window.parent (WP top frame)
            //   · popup   → posts to window.opener.parent (WP top frame) then closes
            // The old check `window.parent !== window` was false in the popup
            // (a popup is a top-level window), so the session was never sent and
            // the popup was never closed.
            if (this.isEmbeddedContext()) {
              await this.postEmbeddedSessionToParent('login', 'oauth');
            }
          } else if (this.oauthProviderError() && !this.isEmbeddedContext()) {
            await this.router.navigate(['/login'], {
              state: { authProviderError: this.oauthProviderError() },
            });
          }
        }
      }
    });
  }

  // ============ STEP 1 — REGISTER (sends verification code) ============
  async registerUser(
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
    password: string,
    marketingConsent: boolean,
  ): Promise<RegisterResult> {
    try {
      const providerCheck = await this.checkProviderAllowed(email, 'email');
      if (!providerCheck.success) return providerCheck;

      const { nextStep, userId } = await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            phone_number: `+57${phone}`,
            given_name: firstName,
            family_name: lastName,
          },
          autoSignIn: true,
        },
      });

      if (nextStep.signUpStep === 'CONFIRM_SIGN_UP') {
        const cognitoSub = userId ?? '';
        this.pendingRegistration = { cognitoSub, email, firstName, lastName, phone, marketingConsent };
        // Pre-register in backend as 'pending' so the profile exists even if the
        // browser is closed before Step 2. Errors are swallowed — this is best-effort.
        this.preRegisterInBackend({ cognitoSub, email, firstName, lastName, phone, marketingConsent });
        return { success: true, needsConfirmation: true };
      }

      // Pool without email verification (COMPLETE_AUTO_SIGN_IN / DONE)
      const { accessToken: token, idToken: idTok } = await this.resolveSessionAfterSignUp(nextStep.signUpStep, email, password);
      if (!token) throw { name: 'NetworkError' };
      this.accessToken.set(token);
      this.idToken.set(idTok ?? null);
      const cognitoSub = userId ?? '';
      const regProfile = await this.saveProfileInBackend({ cognitoSub, email, firstName, lastName, phone, marketingConsent });
      if (regProfile) this.setCurrentUser(regProfile);
      this.saveWpSession();
      this.justLoggedIn.set(true);

      return { success: true };
    } catch (error: any) {
      console.error('Error in registerUser:', error);
      return { success: false, error: this.translateError(error.name, error.message), errorName: error.name };
    }
  }

  // ============ STEP 2 — CONFIRM CODE ============
  async confirmRegistration(email: string, code: string): Promise<RegisterResult> {
    try {
      await confirmSignUp({ username: email, confirmationCode: code });

      // Amplify v6 requires autoSignIn() to complete the session after confirmSignUp().
      // Edge case: if a stale session exists (user didn't sign out before re-registering),
      // autoSignIn() throws UserAlreadyAuthenticatedException — we catch it and proceed
      // straight to fetchAuthSession(), which will return the new user's token because
      // confirmSignUp() already replaced the internal Amplify auth state.
      try {
        await autoSignIn();
      } catch {
        // UserAlreadyAuthenticatedException or similar — session is already active
      }

      const { tokens } = await fetchAuthSession();
      const token = tokens?.accessToken?.toString();
      if (!token) throw { name: 'NetworkError' };
      this.accessToken.set(token);
      this.idToken.set(tokens?.idToken?.toString() ?? null);

      // The user was pre-registered as 'pending' at Step 1.
      // POST /login auto-activates them (findUserBySub promotes pending → active).
      // Fallback to POST /register if pre-registration failed (e.g. network error at Step 1).
      const anonymousId = this.getAnonymousId();
      let confirmProfile: UserProfile | null = null;
      try {
        const loginRes = await firstValueFrom(
          this.http.post<{ success: boolean; data: UserProfile }>(
            `${environment.apiUrl}/client/auth/login`,
            {},
            anonymousId ? { headers: { 'X-Anonymous-Id': anonymousId } } : undefined,
          ),
        );
        confirmProfile = loginRes.data ?? null;
      } catch (loginErr: any) {
        if (loginErr?.status === 404) {
          const pending = this.pendingRegistration!;
          confirmProfile = await this.saveProfileInBackend(pending);
        } else {
          throw loginErr;
        }
      }

      this.pendingRegistration = null;
      if (confirmProfile) this.setCurrentUser(confirmProfile);
      this.saveWpSession();
      this.justLoggedIn.set(true);

      return { success: true };
    } catch (error: any) {
      console.error('Error in confirmRegistration:', error);
      const providerMismatch = this.providerMismatchResult(error);
      if (providerMismatch) return providerMismatch;
      return { success: false, error: this.translateError(error.name, error.message), errorName: error.name };
    }
  }

  // ============ CONFIRM SIGN-UP ONLY (used from login flow) ============
  // Confirms the account and, if a Cognito session is still active (autoSignIn),
  // also syncs the backend: POST /login auto-activates the pending user profile
  // and returns { autoSignedIn: true } so the component can go straight to the panel.
  // When autoSignIn is unavailable (browser was closed between Step 1 and Step 2),
  // we return { success: true } and let the login page handle the activation.
  async confirmSignUpOnly(email: string, code: string): Promise<RegisterResult> {
    try {
      await confirmSignUp({ username: email, confirmationCode: code });

      // Attempt to get a session immediately (works when the browser was not closed)
      try {
        await autoSignIn();
      } catch { /* session expired or unavailable — that's OK */ }

      const { tokens } = await fetchAuthSession();
      const token = tokens?.accessToken?.toString();

      if (token) {
        this.accessToken.set(token);
        const anonymousId = this.getAnonymousId();
        const loginRes = await firstValueFrom(
          this.http.post<{ success: boolean; data: UserProfile }>(
            `${environment.apiUrl}/client/auth/login`,
            {},
            anonymousId ? { headers: { 'X-Anonymous-Id': anonymousId } } : undefined,
          ),
        );
        this.setCurrentUser(loginRes.data ?? null);
        this.justLoggedIn.set(true);
        return { success: true, autoSignedIn: true };
      }

      // No session available — the login page will handle activation after the user signs in
      return { success: true };
    } catch (error: any) {
      console.error('Error in confirmSignUpOnly:', error);
      const providerMismatch = this.providerMismatchResult(error);
      if (providerMismatch) return providerMismatch;
      return { success: false, error: this.translateError(error.name, error.message), errorName: error.name };
    }
  }

  // ============ RESEND VERIFICATION CODE ============
  async resendConfirmationCode(email: string): Promise<RegisterResult> {
    try {
      await resendSignUpCode({ username: email });
      return { success: true };
    } catch (error: any) {
      console.error('Error in resendConfirmationCode:', error);
      return { success: false, error: this.translateError(error.name, error.message), errorName: error.name };
    }
  }

  // ============ TOKEN ACCESS ============
  getAccessToken(): string | null {
    return this.accessToken();
  }

  // ============ LOAD / VERIFY SESSION (used by AuthGuard) ============
  // Primero prueba con Amplify (correo electrónico/sesiones de OAuth). Recurre al token externo
  // almacenado en sessionStorage cuando el usuario ha llegado a través de una redirección de WordPress.
  async loadSessionToken(): Promise<boolean> {
    // Defense-in-depth: check URL param (partition-independent) before the
    // localStorage flag (which may not be visible in the iframe's partition).
    try {
      if (new URLSearchParams(window.location.search).get('force_logout') === '1') {
        console.log('[ELD-AUTH] loadSessionToken: force_logout=1 — omitiendo fetchAuthSession');
        this.accessToken.set(null);
        return false;
      }
    } catch { /* no-op */ }
    // In an OAuth popup, clear any stale logout flag in this (first-party)
    // storage partition — (clientes, clientes). Without this, a previous
    // logout would block the fresh OAuth session from reaching the panel.
    // OAuthPopupComponent already does this on first load; this is a safety net
    // for the case where provideAppInitializer runs before ngOnInit.
    try {
      if (localStorage.getItem('eld_oauth_popup') === '1') {
        localStorage.removeItem(this.FORCE_LOGOUT_KEY);
      }
    } catch { /* no-op */ }
    // If a logout just happened, skip fetchAuthSession() entirely.
    // Calling fetchAuthSession() while Amplify still has localStorage keys can trigger
    // the Hub "signedIn" event, which would auto-post the session to the WP parent frame
    // before ngOnInit gets a chance to check the flag.
    // Do NOT consume the flag here — hasActiveSession() (called from ngOnInit) must do that.
    try {
      if (localStorage.getItem(this.FORCE_LOGOUT_KEY)) {
        this.accessToken.set(null);
        return false;
      }
    } catch { /* no-op */ }
    try {
      const { tokens } = await fetchAuthSession();
      const token = tokens?.accessToken?.toString();

      if (token) {
        this.accessToken.set(token);
        this.idToken.set(tokens?.idToken?.toString() ?? null);
        return true;
      }
    } catch (e) {
      // Sesión de Amplify no disponible — recurrir al token externo
      console.log('[loadSessionToken] Amplify error:', e);
    }

    // Fallback: token recibido de la redirección de WordPress (sobrevive a la actualización de la página mediante sessionStorage)
    const externalToken = sessionStorage.getItem(this.EXTERNAL_TOKEN_KEY);
    if (externalToken) {
      this.accessToken.set(externalToken);
      return true;
    }

    this.accessToken.set(null);
    return false;
  }

  // ============ VERIFY SESSION AGAINST COGNITO SERVER (used by embedded login ngOnInit) ============
  // Unlike loadSessionToken(), this forces a round-trip to Cognito to validate the session.
  // Also respects the FORCE_LOGOUT_KEY flag set by logout() — if that flag is present the modal
  // always shows the form, regardless of what Amplify/Cognito says.
  async hasActiveSession(): Promise<boolean> {
    // Consume the "just logged out" flag first — takes priority over any cached session.
    try {
      if (localStorage.getItem(this.FORCE_LOGOUT_KEY)) {
        localStorage.removeItem(this.FORCE_LOGOUT_KEY);
        this.accessToken.set(null);
        return false;
      }
    } catch { /* no-op */ }

    try {
      const { tokens } = await fetchAuthSession({ forceRefresh: true });
      const token = tokens?.accessToken?.toString();
      if (token) {
        this.accessToken.set(token);
        this.idToken.set(tokens?.idToken?.toString() ?? null);
        return true;
      }
    } catch {
      // forceRefresh throws when there is no valid refresh token (i.e. logged out)
    }
    return false;
  }

  // ============ GOOGLE OAUTH ============
  async loginWithGoogle(email?: string): Promise<ProviderCheckResult> {
    const providerCheck = await this.checkProviderAllowed(email ?? '', 'google');
    if (!providerCheck.success) return providerCheck;

    if (this.isEmbeddedContext()) {
      // Google blocks OAuth inside iframes (HTTP 403 / X-Frame-Options).
      // Open a popup so the authorization flow runs in a full browser window.
      // After Cognito redirects back, the popup posts the session to
      // window.opener.parent (the WordPress top frame) and closes itself.
      // anon_id se pasa para que el popup lo persista en su first-party
      // localStorage ANTES de que Google/Facebook cambie la URL por COOP,
      // asegurando que syncOAuthProfileToBackend() envie el header correcto.
      const anonId = this.getAnonymousId();
      const anonParam = anonId ? `&anon_id=${encodeURIComponent(anonId)}` : '';
      window.open(
        `${window.location.origin}/oauth-popup?provider=google${anonParam}`,
        'eld_oauth',
        'width=520,height=680,resizable=yes,scrollbars=yes'
      );
      return { success: true };
    }
    await signInWithRedirect({ provider: 'Google' });
    return { success: true };
  }

  // ============ FACEBOOK OAUTH ============
  async loginWithFacebook(email?: string): Promise<ProviderCheckResult> {
    const providerCheck = await this.checkProviderAllowed(email ?? '', 'facebook');
    if (!providerCheck.success) return providerCheck;

    if (this.isEmbeddedContext()) {
      // Same iframe restriction applies to Facebook OAuth.
      const anonId = this.getAnonymousId();
      const anonParam = anonId ? `&anon_id=${encodeURIComponent(anonId)}` : '';
      window.open(
        `${window.location.origin}/oauth-popup?provider=facebook${anonParam}`,
        'eld_oauth',
        'width=520,height=680,resizable=yes,scrollbars=yes'
      );
      return { success: true };
    }
    await signInWithRedirect({ provider: 'Facebook' });
    return { success: true };
  }

  // Returns true when the app is running inside an iframe OR in an OAuth popup
  // window opened from the iframe.
  // Uses localStorage (NOT sessionStorage) for the popup flag because
  // sessionStorage is wiped by COOP-based browsing context group changes that
  // Google/Facebook trigger during their OAuth consent flow.
  isEmbeddedContext(): boolean {
    try {
      if (window.self !== window.top) return true;
      if (localStorage.getItem('eld_oauth_popup') === '1') return true;
    } catch { /* cross-origin top frame — treat as embedded */ return true; }
    return false;
  }

  // ============ EMAIL / PASSWORD LOGIN ============
  async loginWithEmail(email: string, password: string): Promise<{ success: boolean; error?: string; errorName?: string }> {
    const providerCheck = await this.checkProviderAllowed(email, 'email');
    if (!providerCheck.success) return providerCheck;

    this.emailLoginInProgress = true;
    try {
      const { isSignedIn, nextStep } = await signIn({ username: email, password });

      // Cognito requires the user to set a new password (admin-created account or temporary password).
      // Amplify v6 surfaces both the internal PASSWORD_VERIFIER SRP step and the subsequent
      // NEW_PASSWORD_REQUIRED challenge as a single signInStep value.
      if (!isSignedIn && nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        // Keep the Amplify challenge session alive so the login component can
        // call confirmSignIn() to let the user set their new permanent password.
        return { success: false, errorName: 'NewPasswordRequired' };
      }

      // Amplify v6 does NOT throw UserNotConfirmedException — instead it returns
      // isSignedIn: false with signInStep 'CONFIRM_SIGN_UP' when the account
      // hasn't been verified yet.
      if (!isSignedIn && nextStep.signInStep === 'CONFIRM_SIGN_UP') {
        signOut().catch(() => {});
        return { success: false, errorName: 'UserNotConfirmedException' };
      }

      const { tokens } = await fetchAuthSession();
      const token = tokens?.accessToken?.toString();
      if (!token) throw { name: 'NetworkError' };
      this.accessToken.set(token);
      this.idToken.set(tokens?.idToken?.toString() ?? null);

      return await this.finalizeClientLogin();
    } catch (error: any) {
      this.accessToken.set(null);
      this.idToken.set(null);
      const providerMismatch = this.providerMismatchResult(error);
      if (providerMismatch) return providerMismatch;
      if (error?.status === 404) {
        signOut().catch(() => {});
        return { success: false, errorName: 'UserProfileNotFound', error: 'No encontramos tu cuenta. Por favor regístrate primero.' };
      }
      return { success: false, error: this.translateError(error.name, error.message), errorName: error.name };
    } finally {
      this.emailLoginInProgress = false;
    }
  }

  // ============ COMPLETE NEW_PASSWORD_REQUIRED CHALLENGE ============
  // Called from the login inline form after the user sets their new password.
  // Amplify must have an active challenge session (loginWithEmail returned
  // NewPasswordRequired without signOut), otherwise this will throw.
  async confirmNewPassword(newPassword: string): Promise<{ success: boolean; error?: string; errorName?: string }> {
    try {
      await confirmSignIn({ challengeResponse: newPassword });
      const { tokens } = await fetchAuthSession();
      const token = tokens?.accessToken?.toString();
      if (!token) throw { name: 'NetworkError' };
      this.accessToken.set(token);
      this.idToken.set(tokens?.idToken?.toString() ?? null);

      return await this.finalizeClientLogin();
    } catch (error: any) {
      this.accessToken.set(null);
      this.idToken.set(null);
      const providerMismatch = this.providerMismatchResult(error);
      if (providerMismatch) return providerMismatch;
      if (error?.status === 404) {
        signOut().catch(() => {});
        return { success: false, errorName: 'UserProfileNotFound', error: 'No encontramos tu cuenta. Por favor regístrate primero.' };
      }
      return { success: false, error: this.translateError(error.name, error.message), errorName: error.name };
    }
  }

  private async finalizeClientLogin(): Promise<{ success: boolean; error?: string; errorName?: string }> {
    const anonymousId = this.getAnonymousId();
    const loginRes = await firstValueFrom(
      this.http.post<{ success: boolean; data: UserProfile }>(
        `${environment.apiUrl}/client/auth/login`,
        {},
        anonymousId ? { headers: { 'X-Anonymous-Id': anonymousId } } : undefined,
      ),
    );
    this.setCurrentUser(loginRes.data ?? null);
    this.saveWpSession();
    this.justLoggedIn.set(true);
    return { success: true };
  }
  // ============ REFRESH PAYWALL COOKIE ============
  // Llama al backend para que refresque la cookie HttpOnly lacronica_paywall
  // con el estado de suscripción actualizado. Se debe llamar:
  //   - Después de cada login exitoso (lo hace saveWpSession internamente)
  //   - Después de una compra de suscripción exitosa
  // Fire-and-forget: no bloquea el flujo si falla.
  refreshPaywallCookie(): void {
    const token = this.accessToken();
    if (!token) return;
    this.http.post(
      `${environment.apiUrl}/client/auth/refresh-cookie`,
      {},
      { withCredentials: true },
    ).subscribe({ error: () => {} });
  }

  // ============ STORE SESSION IN WORDPRESS LOCALSTORAGE ============
  // Writes the current session to localStorage under the same key that
  // events.js reads, so the WordPress UI updates without a page reload
  // when auth happens via the web-component modal (not an iframe).
  async storeSessionLocally(): Promise<void> {
    try {
      const { tokens } = await fetchAuthSession();
      const token = tokens?.accessToken?.toString();
      if (!token) return;
      const user = this.currentUser();
      const displayName = user
        ? `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() || user.email
        : null;
      const sessionData: Record<string, unknown> = { access_token: token };
      if (user) {
        sessionData['user'] = {
          display_name: displayName,
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email,
          has_active_subscription: user.has_active_subscription === true,
        };
      }
      localStorage.setItem('eldiario_session', JSON.stringify(sessionData));
    } catch { /* best-effort */ }
  }

  // ============ CLEAR SESSION (used by interceptor on 401) ============
  clearSession(): void {
    this.accessToken.set(null);
    this.idToken.set(null);
    this.setCurrentUser(null);
    this.pendingRegistration = null;
    localStorage.removeItem('lacronica_session');
    sessionStorage.removeItem(this.EXTERNAL_TOKEN_KEY);
    signOut().catch(() => {});
  }

  // ============ WIPE AMPLIFY STORAGE KEYS (safe, no side effects) ============
  // Eliminates Cognito/Amplify keys from localStorage IN THIS browsing context.
  // Does NOT call signOut() to avoid the Cognito Hosted UI redirect that Amplify
  // can trigger when OAuth is configured (responseType: 'code').  The server-side
  // token revocation already happened via signOut({ global: true }) in logout().
  // Called from provideAppInitializer (before any guard/component) when
  // force_logout=1 is detected in the URL.
  wipeAmplifyStorageKeys(): void {
    this.accessToken.set(null);
    this.idToken.set(null);
    try {
      Object.keys(localStorage).forEach(k => {
        if (
          k.startsWith('CognitoIdentityServiceProvider.') ||
          k.startsWith('amplify-') ||
          k.startsWith('com.amplify') ||
          k.startsWith('aws-amplify')
        ) {
          localStorage.removeItem(k);
        }
      });
    } catch { /* no-op */ }
  }

  // ============ CLEAR AMPLIFY SESSION (called from inside the iframe) ============
  // Clears Amplify tokens in the current browsing context's storage partition.
  // Needed because Chrome Storage Partitioning keeps a separate localStorage for
  // (clientes.cronicadelquindio.com iframe inside cronicadelquindio.com) that
  // is distinct from the first-party (clientes, clientes) partition used by the
  // panel/direct access. logout() cannot reach the iframe's partition.
  // NOTE: does NOT call signOut() — with OAuth configured (responseType: 'code'),
  // Amplify may redirect the iframe to Cognito's logout URL, which would cause the
  // iframe to reload WITHOUT force_logout=1, potentially re-triggering auto-login.
  async clearAmplifySession(): Promise<void> {
    console.log('[ELD-AUTH] clearAmplifySession: limpiando tokens del iframe');
    this.wipeAmplifyStorageKeys();
  }

  // ============ LOGOUT (explicit user action) ============
  async logout(syncServer = true): Promise<void> {
    // Snapshot token BEFORE clearing signals — needed both to call the backend
    // (interceptor uses the signal) and to detect federated users.
    const currentAccessToken = this.accessToken();

    // Revocar el JTI en el backend ANTES de limpiar la señal.
    // withCredentials: true para que el backend pueda limpiar la cookie
    // lacronica_paywall (Domain=.cronicadelquindio.com) en la respuesta.
    // Best-effort: si falla, la sesión local se limpia igualmente.
    if (syncServer) {
      try {
        await firstValueFrom(
          this.http.post(`${environment.apiUrl}/client/auth/logout`, {}, { withCredentials: true })
        );
      } catch { /* no-op — session cleared regardless */ }
    }

    this.accessToken.set(null);
    this.idToken.set(null);
    this.setCurrentUser(null);
    this.pendingRegistration = null;
    localStorage.removeItem('lacronica_session');
    sessionStorage.removeItem(this.EXTERNAL_TOKEN_KEY);

    // Detect federated user (Google, etc.) by inspecting the access token scope.
    // Native Cognito users have scope "aws.cognito.signin.user.admin openid profile email".
    // Federated users only have "openid profile email" — GlobalSignOut would fail with 400.
    // Additionally, Amplify v6's signOut() for OAuth users triggers a hosted-UI redirect
    // that conflicts with the component's own WordPress redirect, causing a race condition.
    // For federated users we skip both Amplify signOut calls and rely on manual cleanup.
    let isFederated = false;
    try {
      if (currentAccessToken) {
        const payload = JSON.parse(atob(currentAccessToken.split('.')[1]));
        const scopes: string = payload?.scope ?? '';
        isFederated = !scopes.includes('aws.cognito.signin.user.admin');
      }
    } catch { /* ignore — if we can't decode, assume native and try normally */ }

    if (!isFederated) {
      try {
        // global: true revokes the refresh token server-side and clears hosted-UI cookies.
        // Works only for native Cognito users (email/password).
        await signOut({ global: true });
      } catch {
        // Network error or other unexpected failure — fall back to local sign-out
        try { await signOut(); } catch { /* no-op */ }
      }
    }
    // Usuarios federados (Google/Facebook): NO se llama signOut().
    // signOut() desencadena un redirect al hosted UI de Cognito (/logout?client_id=...),
    // que cancela nuestra navegación a WordPress con ?px_auth=logout, impidiendo que
    // events.js limpie la sesión del menú y el usuario siga apareciendo como logueado.
    // Las claves de Amplify se limpian manualmente abajo — deja el estado limpio
    // para que el próximo login con Google funcione sin encontrar tokens stale.

    // Belt-and-suspenders: limpiar manualmente claves de Amplify/Cognito del localStorage.
    // Necesario siempre (usuarios nativos y federados) por edge cases de Amplify v6.
    // Para federados es la única limpieza de Amplify que se hace.
    try {
      Object.keys(localStorage).forEach(k => {
        if (k.startsWith('CognitoIdentityServiceProvider.') || k.startsWith('amplify-')) {
          localStorage.removeItem(k);
        }
      });
    } catch { /* no-op */ }

    // Mark that a logout just happened. Consumed by hasActiveSession() to prevent
    // the login modal from silently re-authenticating on its very next open.
    try { localStorage.setItem(this.FORCE_LOGOUT_KEY, '1'); } catch { /* no-op */ }
  }

  // ============ PRIVATE HELPERS ============
  private async resolveSessionAfterSignUp(
    step: string,
    email: string,
    password: string,
  ): Promise<{ accessToken: string | undefined; idToken: string | undefined }> {
    if (step === 'COMPLETE_AUTO_SIGN_IN') {
      await autoSignIn();
    } else if (step === 'DONE') {
      await signIn({ username: email, password });
    }
    const { tokens } = await fetchAuthSession();
    return {
      accessToken: tokens?.accessToken?.toString(),
      idToken: tokens?.idToken?.toString(),
    };
  }

  private async saveProfileInBackend(data: PendingRegistration): Promise<UserProfile | null> {
    const anonymousId = this.getAnonymousId();
    const res = await firstValueFrom(
      this.http.post<{ success: boolean; data: UserProfile }>(
        `${environment.apiUrl}/client/auth/register`,
        {
          email: data.email,
          first_name: data.firstName,
          last_name: data.lastName,
          phone_number: data.phone,
          marketing_consent: data.marketingConsent ? 1 : 0,
        },
        anonymousId ? { headers: { 'X-Anonymous-Id': anonymousId } } : undefined,
      ),
    );
    return res.data ?? null;
  }

  // Calls the public /pre-register endpoint (no JWT) right after Cognito signUp().
  // Saves the user as 'pending' in the DB so the profile survives browser close.
  // Fire-and-forget — errors are logged but never propagate to the caller.
  private preRegisterInBackend(data: PendingRegistration): void {
    firstValueFrom(
      this.http.post<{ success: boolean }>(
        `${environment.apiUrl}/client/auth/pre-register`,
        {
          cognito_sub: data.cognitoSub,
          email: data.email,
          first_name: data.firstName,
          last_name: data.lastName,
          phone_number: data.phone,
          marketing_consent: data.marketingConsent ? 1 : 0,
        },
      ),
    ).catch(err => console.warn('[preRegisterInBackend] Non-blocking error:', err));
  }

  // Called during auto-restore (embed ngOnInit) to sync with the backend and
  // trigger the anonymous → authenticated-user event migration.
  async ensureBackendLogin(): Promise<void> {
    const anonymousId = this.getAnonymousId();
    try {
      const res = await firstValueFrom(
        this.http.post<{ success: boolean; data: UserProfile }>(
          `${environment.apiUrl}/client/auth/login`,
          {},
          anonymousId ? { headers: { 'X-Anonymous-Id': anonymousId } } : undefined,
        )
      );
      this.setCurrentUser(res.data ?? null);
    } catch { /* best-effort — profile will be loaded by events flow */ }
  }

  // Syncs an OAuth (Google/Facebook) user with the backend.
  // Returning user → POST /login. New user (404) → auto-create profile via POST /register.
  // Returns true if the session is valid and ready, false on unrecoverable error.
  private async syncOAuthProfileToBackend(): Promise<boolean> {
    try {
      // Returning OAuth user — confirm profile exists in DB
      const anonymousId = this.getAnonymousId();
      const oauthLoginRes = await firstValueFrom(
        this.http.post<{ success: boolean; data: UserProfile }>(
          `${environment.apiUrl}/client/auth/login`,
          {},
          anonymousId ? { headers: { 'X-Anonymous-Id': anonymousId } } : undefined,
        )
      );
      this.setCurrentUser(oauthLoginRes.data ?? null);
      return true;
    } catch (loginError: any) {
      if (this.isProviderMismatchError(loginError)) {
        this.rejectOAuthProviderMismatch(loginError);
        return false;
      }
      if (loginError?.status === 404) {
        // New OAuth user — create profile automatically using ID token claims
        try {
          const { tokens } = await fetchAuthSession();
          const claims = tokens?.idToken?.payload;

          // Detectar el proveedor OAuth desde cognito:username.
          // Cognito formatea el username federado como "Google_<id>" o "Facebook_<id>".
          const cognitoUsername = String(claims?.['cognito:username'] ?? '').toLowerCase();
          const loginMethod = cognitoUsername.startsWith('facebook') ? 'facebook'
                            : cognitoUsername.startsWith('google')   ? 'google'
                            : 'email';

          const anonymousId = this.getAnonymousId();
          const oauthRegRes = await firstValueFrom(
            this.http.post<{ success: boolean; data: UserProfile }>(
              `${environment.apiUrl}/client/auth/register`,
              {
                email:             (claims?.['email']        as string) ?? '',
                first_name:        (claims?.['given_name']   as string) ?? '',
                last_name:         (claims?.['family_name']  as string) ?? '',
                phone_number:      (claims?.['phone_number'] as string) ?? null,
                marketing_consent: 0,
                login_method:      loginMethod,
              },
              anonymousId ? { headers: { 'X-Anonymous-Id': anonymousId } } : undefined,
            ),
          );
          this.setCurrentUser(oauthRegRes.data ?? null);
          return true;
        } catch (registerError: any) {
          if (this.isProviderMismatchError(registerError)) {
            this.rejectOAuthProviderMismatch(registerError);
            return false;
          }
          console.error('Error creating OAuth profile:', registerError);
          return false;
        }
      }
      console.error('Error syncing OAuth profile:', loginError);
      return false;
    }
  }

  /** Preflight for flows where the browser already knows the email. */
  private async checkProviderAllowed(email: string, requestedLoginMethod: 'email' | 'google' | 'facebook'): Promise<ProviderCheckResult> {
    const normalizedEmail = String(email ?? '').trim();
    if (!normalizedEmail) return { success: true };

    try {
      await firstValueFrom(
        this.http.post<{ success: boolean; allowed: boolean }>(
          `${environment.apiUrl}/client/auth/provider-check`,
          { email: normalizedEmail, requested_login_method: requestedLoginMethod },
        ),
      );
      return { success: true };
    } catch (error: any) {
      const mismatch = this.providerMismatchResult(error);
      if (mismatch) return mismatch;
      // The authenticated backend guard remains authoritative after sign-in.
      // Do not make Cognito unavailable because this preflight is temporarily down.
      return { success: true };
    }
  }

  private isProviderMismatchError(error: any): boolean {
    return error?.status === 409 && error?.error?.code === 'AUTH_PROVIDER_MISMATCH';
  }

  private providerMismatchResult(error: any): ProviderCheckResult | null {
    if (!this.isProviderMismatchError(error)) return null;
    return {
      success: false,
      errorName: 'AUTH_PROVIDER_MISMATCH',
      error: String(error.error?.message ?? 'Usa el método con el que creaste tu cuenta.'),
    };
  }

  private rejectOAuthProviderMismatch(error: any): void {
    const message = String(error?.error?.message ?? 'Usa el método con el que creaste tu cuenta.');
    this.oauthProviderError.set(message);
    try { sessionStorage.setItem(this.PROVIDER_MISMATCH_KEY, message); } catch { /* no-op */ }

    // The backend deleted the non-canonical account. Avoid a Hosted UI redirect
    // here because it would hide the error in the popup/direct-login flow.
    this.accessToken.set(null);
    this.idToken.set(null);
    this.setCurrentUser(null);
    this.pendingRegistration = null;
    try { localStorage.removeItem('lacronica_session'); } catch { /* no-op */ }
    sessionStorage.removeItem(this.EXTERNAL_TOKEN_KEY);
    this.wipeAmplifyStorageKeys();
  }

  consumeProviderMismatchMessage(): string | null {
    const inMemory = this.oauthProviderError();
    this.oauthProviderError.set(null);
    try {
      const persisted = sessionStorage.getItem(this.PROVIDER_MISMATCH_KEY);
      sessionStorage.removeItem(this.PROVIDER_MISMATCH_KEY);
      return inMemory ?? persisted;
    } catch {
      return inMemory;
    }
  }

  // ============ GET PROFILE ============
  async getProfile(): Promise<boolean> {
    try {
      const res = await firstValueFrom(
        this.http.get<{ success: boolean; data: UserProfile }>(`${environment.apiUrl}/usuarios/perfil`)
      );
      this.setCurrentUser(res.data ?? null);
      return true;
    } catch {
      return false;
    }
  }

  // ============ UPDATE PROFILE ============
  async updateProfile(data: UpdateProfilePayload): Promise<{ success: boolean; error?: string }> {
    try {
      const res = await firstValueFrom(
        this.http.put<{ success: boolean; data: UserProfile }>(`${environment.apiUrl}/usuarios/perfil`, data)
      );
      this.setCurrentUser(res.data ?? null);
      return { success: true };
    } catch {
      return { success: false, error: 'No se pudo actualizar el perfil. Intenta de nuevo.' };
    }
  }

  async deleteAccount(): Promise<{ success: boolean; error?: string }> {
    try {
      await firstValueFrom(
        this.http.delete<{ success: boolean }>(`${environment.apiUrl}/usuarios/perfil`, { withCredentials: true }),
      );
      // The backend revoked the token as part of the deletion. Clear only the
      // local session; a protected logout request would correctly be rejected.
      await this.logout(false);
      return { success: true };
    } catch (err: any) {
      return {
        success: false,
        error: err?.error?.message ?? 'No se pudo eliminar la cuenta. Intenta nuevamente o contacta soporte.',
      };
    }
  }

  // ============ UPDATE NEWSLETTER CONSENT ============
  async updateNewsletterConsent(value: boolean): Promise<{ success: boolean; error?: string }> {
    try {
      await firstValueFrom(
        this.http.put<{ success: boolean }>(`${environment.apiUrl}/usuarios/newsletter-consent`, { newsletter_consent: value })
      );
      const current = this.currentUser();
      if (current) this.currentUser.set({ ...current, marketing_consent: value });
      return { success: true };
    } catch {
      return { success: false, error: 'No se pudo guardar la preferencia. Intenta de nuevo.' };
    }
  }

  // ============ UPDATE DOCUMENT INFO ============
  async updateDocumentInfo(
    docType: 'CC' | 'TI' | 'CE' | 'PA' | 'NIT' | null,
    docNumber: string | null,
  ): Promise<{ success: boolean; error?: string }> {
    try {
      await firstValueFrom(
        this.http.put<{ success: boolean }>(`${environment.apiUrl}/usuarios/documento`, {
          doc_type: docType,
          doc_number: docNumber,
        })
      );
      const current = this.currentUser();
      if (current) this.currentUser.set({ ...current, doc_type: docType, doc_number: docNumber });
      return { success: true };
    } catch {
      return { success: false, error: 'No se pudo guardar el documento. Intenta de nuevo.' };
    }
  }

  // ============ CHANGE PASSWORD (Cognito) ============
  async changePassword(oldPassword: string, newPassword: string): Promise<{ success: boolean; error?: string; errorName?: string }> {
    try {
      await updatePassword({ oldPassword, newPassword });
      return { success: true };
    } catch (error: any) {
      const message = error?.name === 'NotAuthorizedException'
        && !String(error?.message ?? '').toLowerCase().includes('password attempts exceeded')
        && !String(error?.message ?? '').toLowerCase().includes('attempt limit exceeded')
        ? 'La contraseña actual es incorrecta'
        : this.translateError(error.name, error.message);
      return { success: false, error: message, errorName: error.name };
    }
  }

  // ============ FORGOT PASSWORD — STEP 1 (send code) ============
  async forgotPassword(email: string): Promise<{ success: boolean; error?: string; errorName?: string }> {
    try {
      await resetPassword({ username: email });
      return { success: true };
    } catch (error: any) {
      // Return success even for UserNotFoundException to prevent email enumeration (OWASP A07)
      if (error?.name === 'UserNotFoundException') return { success: true };
      return { success: false, error: this.translateError(error.name, error.message), errorName: error.name };
    }
  }

  // ============ FORGOT PASSWORD — STEP 2 (confirm code + new password) ============
  async confirmForgotPassword(
    email: string,
    code: string,
    newPassword: string,
  ): Promise<{ success: boolean; error?: string; errorName?: string }> {
    try {
      await confirmResetPassword({ username: email, confirmationCode: code, newPassword });
      return { success: true };
    } catch (error: any) {
      return { success: false, error: this.translateError(error.name, error.message), errorName: error.name };
    }
  }

  // ============ CONFIGURACIÓN DEL TOKEN DESDE UNA FUENTE EXTERNA (redireccionamiento de WordPress a Angular) ============
  // Llamada por tokenFromUrlGuard cuando el usuario accede con ?access_token=XXX.
  // Almacena el token en la señal (que el interceptor HTTP utiliza de inmediato)
  // y en sessionStorage (para que se mantenga tras una actualización de la página en la misma pestaña).
  // También elimina FORCE_LOGOUT_KEY: si WordPress está pasando un token fresco
  // significa que el usuario acaba de hacer login en el iframe, así que cualquier
  // flag de logout previo en esta partición de storage (clientes, clientes) es obsoleto.
  // Sin esto, loadSessionToken() en authGuard encontraría el flag y devolvería false,
  // bloqueando el acceso al panel en el segundo login tras un logout.
  setTokenFromExternal(accessToken: string): void {
    this.accessToken.set(accessToken);
    sessionStorage.setItem(this.EXTERNAL_TOKEN_KEY, accessToken);
    try { localStorage.removeItem(this.FORCE_LOGOUT_KEY); } catch { /* no-op */ }
  }

  // ============ POST EMBEDDED SESSION TO PARENT (cierre del modal WordPress) ============
  // Llamado por login/register/forgot-password cuando corren dentro del iframe de WordPress,
  // y también desde OAuthPopupComponent (vía Hub listener) cuando el popup termina el flujo OAuth.
  //
  // Tres contextos posibles:
  //   a) iframe embebido en WP           → window.parent es el top frame de WP
  //   b) popup OAuth, opener válido      → window.opener existe (COOP no lo eliminó)
  //   c) popup OAuth, opener null        → Google/Facebook COOP severs window.opener;
  //                                        navegar popup a WP con sesión en hash fragment;
  //                                        events.js la decodifica, escribe en WP localStorage
  //                                        (dispara storage event en la pestaña WP padre),
  //                                        cierra el modal, e intenta window.close()
  async postEmbeddedSessionToParent(authEvent?: 'login' | 'register', method?: string): Promise<void> {
    if (this.currentUser()) this.saveWpSession();
    const sessionData = JSON.parse(localStorage.getItem('lacronica_session') || '{}');
    const payload = authEvent
      ? { type: 'eldiario:session', payload: sessionData, auth_event: authEvent, auth_method: method ?? 'unknown' }
      : { type: 'eldiario:session', payload: sessionData };

    // Caso (b) y (c): popup OAuth
    const isPopup = localStorage.getItem('eld_oauth_popup') === '1';
    if (isPopup) {
      try { localStorage.removeItem('eld_oauth_popup'); } catch { /* no-op */ }

      // Intento (b): opener no fue eliminado por COOP (puede ocurrir con proveedores
      // que no usan COOP strict, o si la cadena de redirección no lo separó)
      try {
        if (window.opener) {
          // opener es el iframe (mismo origen); su padre es el top frame de WP
          const target: Window = (window.opener as any).parent ?? window.opener;
          target.postMessage(payload, environment.wordpressUrl);
          try { window.close(); } catch { /* no-op */ }
          return;
        }
      } catch { /* no-op */ }

      // Caso (c): window.opener es null — COOP de Google/Facebook lo eliminó.
      // Navegar el popup a WP con la sesión en el hash (nunca enviado al servidor).
      // events.js detecta #eld_auth=, guarda en WP localStorage, dispara el evento
      // storage en la pestaña WP padre (eso cierra el modal), y cierra el popup.
      // Si hay authEvent, se agrega ?px_auth=... antes del hash para que events.js
      // lo procese en la carga fresca del popup y dispare el evento de analytics.
      try {
        const encoded = encodeURIComponent(JSON.stringify(sessionData));
        const authParam = authEvent ? `?px_auth=${authEvent}&method=${method ?? 'oauth'}` : '';
        window.location.replace(`${environment.wordpressUrl}${authParam}#eld_auth=${encoded}`);
      } catch {
        try { window.close(); } catch { /* no-op */ }
      }
      return;
    }

    // Caso (a): iframe embebido — postear directo al top frame de WP
    window.parent.postMessage(payload, environment.wordpressUrl);
  }

  // ============ WORDPRESS SESSION ============
  private saveWpSession(): void {
    const accessToken = this.accessToken();
    const idToken = this.idToken();
    const user = this.currentUser();
    if (!accessToken || !user) return;
    const displayName = `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() || user.email;
    localStorage.setItem('lacronica_session', JSON.stringify({
      access_token: accessToken,
      id_token: idToken ?? '',
      user: {
        id: user.id,
        display_name: displayName,
        email: user.email,
        has_active_subscription: user.has_active_subscription === true,
      },
    }));
    // Refrescar la cookie de paywall cada vez que se guarda la sesión de WP,
    // es decir, después de cada login/register. Fire-and-forget.
    this.refreshPaywallCookie();
  }

  private getAnonymousId(): string {
    const key = 'px_anon_id';
    try {
      try {
        const params = new URLSearchParams(window.location.search);
        const fromQuery = (params.get('anon_id') || params.get('anonymous_id') || params.get('px_anon_id') || '').trim();
        if (fromQuery.length === 36) {
          localStorage.setItem(key, fromQuery);
          return fromQuery;
        }
      } catch {}

      const existing = localStorage.getItem(key);
      if (existing && existing.trim().length === 36) return existing.trim();

      const created = this.createUuid();
      localStorage.setItem(key, created);
      return created;
    } catch {
      return '';
    }
  }

  private createUuid(): string {
    try {
      const c = (globalThis as any).crypto;
      if (c && typeof c.randomUUID === 'function') return String(c.randomUUID());
    } catch {}

    const buf = new Uint8Array(16);
    try {
      const c = (globalThis as any).crypto;
      if (c && typeof c.getRandomValues === 'function') c.getRandomValues(buf);
      else for (let i = 0; i < buf.length; i++) buf[i] = Math.floor(Math.random() * 256);
    } catch {
      for (let i = 0; i < buf.length; i++) buf[i] = Math.floor(Math.random() * 256);
    }

    buf[6] = (buf[6] & 0x0f) | 0x40;
    buf[8] = (buf[8] & 0x3f) | 0x80;

    const hex = Array.from(buf, b => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  // ============ TRANSLATE ERRORS ============
  private translateError(errorName: string, errorMessage?: string): string {
    const message = String(errorMessage ?? '').toLowerCase();

    // Cognito returns NotAuthorizedException for both wrong password and lockout.
    // Distinguish lockout by message so the UI can ask the user to wait.
    if (
      errorName === 'NotAuthorizedException' &&
      (message.includes('password attempts exceeded') || message.includes('attempt limit exceeded'))
    ) {
      return 'Demasiados intentos fallidos. Espera unos minutos e inténtalo de nuevo.';
    }

    const errors: Record<string, string> = {
      // Register
      UsernameExistsException: 'Este correo ya está registrado',
      InvalidPasswordException: 'La contraseña no cumple los requisitos mínimos',
      InvalidParameterException: 'Datos inválidos, revisa los campos',
      CodeMismatchException: 'Código incorrecto, inténtalo de nuevo',
      ExpiredCodeException: 'El código ha expirado, solicita uno nuevo',
      // Login — NotAuthorizedException y UserNotFoundException (credenciales inválidas)
      // comparten mensaje para evitar enumeración de usuarios (OWASP A07)
      NotAuthorizedException: 'Correo o contraseña incorrectos',
      UserNotFoundException: 'Correo o contraseña incorrectos',
      UserNotConfirmedException: 'Debes verificar tu correo antes de continuar',
      PasswordResetRequiredException: 'Debes restablecer tu contraseña',
      // Shared
      LimitExceededException: 'Demasiados intentos, espera un momento',
      TooManyRequestsException: 'Demasiados intentos, espera un momento',
      NetworkError: 'Error de conexión, verifica tu internet',
    };
    return errors[errorName] ?? 'Ocurrió un error inesperado';
  }
}
